import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AppPreferences } from '../core/services/app-preferences';
import { LandingHeader } from '../landing-page/components/landing-header/landing-header';
import { LeadTrackingState } from '../lead-tracking/lead-tracking-state';
import { FundCalculatorApi } from '../personal-fund-request/services/fund-calculator-api';
import { AppIcon } from '../shared/app-icon/app-icon';

interface RequestCard {
  requestId: string;
  title: string;
  status: string;
  statusClass: string;
  date: string;
  amount: string;
  installment: string;
  duration: string;
  tag: string;
  showTag: boolean;
}

@Component({
  selector: 'app-my-requests',
  imports: [LandingHeader, AppIcon],
  templateUrl: './my-requests.html',
  styleUrl: './my-requests.scss',
})
export class MyRequestsComponent implements OnInit {
  readonly preferences = inject(AppPreferences);
  private readonly router = inject(Router);
  private readonly trackingState = inject(LeadTrackingState);
  private readonly fundCalculatorApi = inject(FundCalculatorApi);

  requests: RequestCard[] = [];
  isLoading = true;
  errorMessage = '';
  private requestsResponse: unknown = null;

  constructor() {
    effect(() => {
      this.preferences.language();

      if (this.requestsResponse) {
        this.requests = this.normalizeRequests(this.requestsResponse);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    if (!this.fundCalculatorApi.hasLeadAuthSession()) {
      await this.router.navigateByUrl('/');
      return;
    }

    try {
      const response = await firstValueFrom(this.fundCalculatorApi.getLeadRequests());

      this.requestsResponse = response;
      this.requests = this.normalizeRequests(response);
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  async goHome(): Promise<void> {
    await this.router.navigateByUrl('/');
  }

  async openRequestDetails(request: RequestCard): Promise<void> {
    if (!request.requestId) {
      return;
    }

    this.trackingState.setRequestId(request.requestId);
    await this.router.navigateByUrl('/request-details');
  }

  private normalizeRequests(response: unknown): RequestCard[] {
    const body = this.toRecord(response);
    const data = body ? body['data'] ?? body['Data'] ?? response : response;
    const dataRecord = this.toRecord(data);
    const rawRequests =
      (dataRecord?.['requests'] ?? dataRecord?.['Requests'] ?? dataRecord?.['request'] ?? dataRecord?.['Request']) ??
      data;
    const list = Array.isArray(rawRequests) ? rawRequests : rawRequests ? [rawRequests] : [];

    return list.map((item) => this.toRequestCard(item));
  }

  private toRequestCard(item: unknown): RequestCard {
    const request = this.toRecord(item) ?? {};
    const selectedOfferValue = this.getCaseInsensitiveValue(request, 'selectedOffer');
    const isWithoutOffer = selectedOfferValue === null || selectedOfferValue === undefined;
    const selectedOffer = this.toRecord(
      selectedOfferValue,
    );
    const amount = this.getNumberField(request, [
      'fundAmount',
      'amount',
      'financeAmount',
      'totalFundAmount',
      'requestAmount',
    ]);
    const installment =
      this.getNumberField(selectedOffer ?? {}, ['installmentAmount']) ??
      this.getNumberField(request, [
        'installmentAmount',
        'monthlyInstallment',
        'monthlyInstallmentAmount',
        'installment',
      ]);
    const duration = this.getNumberField(request, [
      'fundPeriodInMonths',
      'fundPeriod',
      'duration',
      'durationInMonths',
      'periodInMonths',
    ]) ?? this.getNumberField(selectedOffer ?? {}, ['fundPeriod']);

    return {
      requestId: this.getStringField(request, ['requestId', 'id', 'leadId']),
      title:
        this.getLocalizedField(request, 'fundType') ||
        this.getStringField(request, ['title', 'offerTitle', 'requestType']) ||
        this.preferences.t('myRequests.defaultRequestTitle'),
      status:
        this.getLocalizedField(request, 'status') ||
        this.getStringField(request, ['status', 'applicationStatus', 'requestStatus']) ||
        this.preferences.t('myRequests.defaultStatus'),
      statusClass: this.getStatusClass(request),
      date: this.formatDate(
        this.getStringField(request, ['createdAt', 'createdOn', 'creationDate', 'requestDate', 'date']),
      ),
      amount: this.formatNumber(amount),
      installment: this.formatNumber(installment),
      duration: `${duration ?? 48} ${this.preferences.t('financeLabels.month')}`,
      tag: isWithoutOffer ? this.preferences.t('myRequests.withoutOfferTag') :
        this.getLocalizedField(request, 'tag') ||
        this.getStringField(request, ['tag', 'label', 'offerStatus']) ||
        this.preferences.t('myRequests.withoutOfferTag'),
      showTag: isWithoutOffer,
    };
  }

  private getStatusClass(request: Record<string, unknown>): string {
    const statusRecord = this.toRecord(this.getCaseInsensitiveValue(request, 'status'));
    const currentHistoryStatus = this.getCurrentHistoryStatus(request);
    const source = currentHistoryStatus ?? statusRecord;

    if (this.getBooleanField(source ?? request, ['isRejected'])) {
      return 'cancelled';
    }

    const englishName = this.getLocalizedEnglishName(source) ||
      this.getStringField(request, ['status', 'applicationStatus', 'requestStatus']);
    const normalizedName = englishName.trim().toLowerCase();

    if (normalizedName === 'under study' ||
      normalizedName === 'pending documents' ||
      normalizedName === 'submitted to financing entity') {
      return 'inprogress';
    }

    if (normalizedName === 'accepted') {
      return 'approved';
    }

    return '';
  }

  private getCurrentHistoryStatus(request: Record<string, unknown>): Record<string, unknown> | null {
    const historyValue =
      this.getCaseInsensitiveValue(request, 'statusHistory') ??
      this.getCaseInsensitiveValue(request, 'statuses') ??
      this.getCaseInsensitiveValue(request, 'requestStatuses');
    const history = Array.isArray(historyValue) ? historyValue : [];
    const current = history.find((item) => {
      const status = this.toRecord(item);

      return status ? this.getBooleanField(status, ['isCurrent']) : false;
    });

    return this.toRecord(current);
  }

  private getLocalizedEnglishName(value: Record<string, unknown> | null): string {
    if (!value) {
      return '';
    }

    const name = this.toRecord(this.getCaseInsensitiveValue(value, 'name'));
    const englishName = name?.['en'] ?? value['en'];

    return typeof englishName === 'string' ? englishName : '';
  }

  private formatDate(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = String(hours % 12 || 12).padStart(2, '0');

    return `${period} ${hour12}:${minutes} ${day}/${month}/${year}`;
  }

  private formatNumber(value: number | null): string {
    if (value === null) {
      return '0';
    }

    return Math.round(value).toLocaleString('en-US');
  }

  private getNumberField(body: Record<string, unknown>, fields: string[]): number | null {
    for (const field of fields) {
      const value = this.getCaseInsensitiveValue(body, field);

      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if (typeof value === 'string' && value.trim()) {
        const numericValue = Number(value.replace(/,/g, ''));

        if (Number.isFinite(numericValue)) {
          return numericValue;
        }
      }
    }

    return null;
  }

  private getStringField(body: Record<string, unknown>, fields: string[]): string {
    for (const field of fields) {
      const value = this.getCaseInsensitiveValue(body, field);

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return '';
  }

  private getBooleanField(body: Record<string, unknown>, fields: string[]): boolean {
    for (const field of fields) {
      const value = this.getCaseInsensitiveValue(body, field);

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (normalizedValue === 'true') {
          return true;
        }
      }
    }

    return false;
  }

  private getLocalizedField(body: Record<string, unknown>, field: string): string {
    const value = this.getCaseInsensitiveValue(body, field);
    const localizedValue = this.toRecord(value);

    if (!localizedValue) {
      return typeof value === 'string' ? value : '';
    }

    const language = this.preferences.language();
    const preferredValue = localizedValue[language];
    const fallbackValue = localizedValue[language === 'ar' ? 'en' : 'ar'];

    if (typeof preferredValue === 'string' && preferredValue.trim()) {
      return preferredValue.trim();
    }

    return typeof fallbackValue === 'string' ? fallbackValue.trim() : '';
  }

  private getCaseInsensitiveValue(body: Record<string, unknown>, field: string): unknown {
    return (
      body[field] ??
      body[field.charAt(0).toUpperCase() + field.slice(1)] ??
      body[field.toLowerCase()]
    );
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return this.extractApiMessage(error.error) || this.preferences.t('common.states.error');
    }

    return error instanceof Error ? error.message : this.preferences.t('common.states.error');
  }

  private extractApiMessage(errorBody: unknown): string {
    const body = this.toRecord(errorBody);
    const message = body?.['message'];

    return typeof message === 'string' ? message : '';
  }
}
